import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Trash2 as DeleteIcon,
  MapPin as LocationIcon,
  DollarSign as MoneyIcon,
  Linkedin as LinkedInIcon,
  Map as MapIcon,
  Phone as PhoneIcon,
  Mail as EmailIcon,
  Mic,
  CheckCircle,
  X,
  Lock as LockIcon,
  Zap as ZapIcon,
  Copy as CopyIcon,
  Check,
} from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { designSystem } from '../theme';

const generateEmailPatterns = (company, name) => {
  if (!company) return [];
  
  const domain = company.toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 20) + '.com';
  
  const contactName = name || 'Hiring Manager';
  const firstName = contactName.split(' ')[0]?.toLowerCase() || 'hiring';
  const lastName = contactName.split(' ')[1]?.toLowerCase() || 'manager';
  
  return [
    `${firstName}.${lastName}@${domain}`,
    `${firstName}${lastName}@${domain}`,
    `${firstName[0]}${lastName}@${domain}`,
    `careers@${domain}`,
    `jobs@${domain}`,
    `hr@${domain}`,
  ];
};

const getGoogleMapsUrl = (company, location) => {
  const query = encodeURIComponent(`${company || ''} ${location || ''}`.trim());
  return `https://www.google.com/maps/search/${query}`;
};

const cleanLinkedInQueryPart = (value) => {
  if (!value) return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, 80);
};

const getLinkedInSearchUrl = (jobOrCompany) => {
  const job = typeof jobOrCompany === 'string' ? { company: jobOrCompany } : (jobOrCompany || {});
  const company = cleanLinkedInQueryPart(job.company);
  if (!company) return 'https://www.linkedin.com';

  const title = cleanLinkedInQueryPart(job.title);
  const location = cleanLinkedInQueryPart(job.location);
  const category = cleanLinkedInQueryPart(job.category);
  const parts = [
    `"${company}"`,
    '"recruiter" OR "talent acquisition" OR "sourcer" OR "hiring manager"',
  ];

  if (title) parts.push(`"${title}"`);
  if (category) parts.push(`"${category}"`);
  if (location && !location.toLowerCase().includes('remote')) parts.push(`"${location}"`);
  parts.push('"United States"');

  const query = encodeURIComponent(parts.join(' '));
  return `https://www.linkedin.com/search/results/people/?keywords=${query}`;
};

function JobList({ jobs, onEdit, onUpdate, onDelete }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [copiedScript, setCopiedScript] = useState(false);
  const [isEditingScript, setIsEditingScript] = useState(false);
  const [editingScriptJobId, setEditingScriptJobId] = useState(null);
  const [editedScript, setEditedScript] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const { isPremium: isPro, startCheckout, loading: subscriptionLoading } = useSubscription();

  const formatSalary = (min, max, offered) => {
    const fmt = (n) => n ? `$${Math.round(n).toLocaleString()}` : '';
    
    if (offered) return `Offered: ${fmt(offered)}`;
    if (min && max) return `${fmt(min)} - ${fmt(max)}`;
    if (min) return `From ${fmt(min)}`;
    if (max) return `Up to ${fmt(max)}`;
    return null;
  };

  const generateScript = (job) => {
    const hours = job.total_experience_hours || 0;
    let experienceText = '';
    
    if (hours > 0) {
      const years = Math.round((hours / 2000) * 10) / 10;
      if (years >= 1) {
        experienceText = ` I have ${years} years of directly relevant experience.`;
      } else {
        const months = Math.round((hours / 2000) * 12);
        if (months > 0) {
          experienceText = ` I have ${months} months of directly relevant experience.`;
        }
      }
    }
    
    return `Hi, I'm following up on the ${job.title} position at ${job.company}. Who would be the best person to speak with about this role?${experienceText}`;
  };

  const generateEmailBody = (job, contactName) => {
    const hours = job.total_experience_hours || 0;
    let experienceText = '';
    
    if (hours > 0) {
      const years = Math.round((hours / 2000) * 10) / 10;
      if (years >= 1) {
        experienceText = `${years} years`;
      } else {
        const months = Math.round((hours / 2000) * 12);
        if (months > 0) {
          experienceText = `${months} months`;
        }
      }
    }

    // Build skills text from matched skills
    const skills = job.matched_skills || [];
    const skillsText = skills.length > 0 
      ? `My key skills include ${skills.slice(0, 5).join(', ')}.`
      : '';

    const greeting = contactName && contactName !== 'Hiring Manager' 
      ? `Hi ${contactName.split(' ')[0]},` 
      : 'Hi,';

    const bodyParts = [
      greeting,
      '',
      `I'm writing to express my interest in the ${job.title} position at ${job.company}.`,
      '',
    ];
    
    // Add experience/skills line only if we have something
    const expSkillLine = experienceText 
      ? `I have ${experienceText} of directly relevant experience. ${skillsText}` 
      : skillsText;
    if (expSkillLine) {
      bodyParts.push(expSkillLine);
      bodyParts.push('');
    }
    
    bodyParts.push('I would welcome the opportunity to discuss how my background aligns with your team\'s needs.');
    bodyParts.push('');
    bodyParts.push('Best regards');

    return bodyParts.join('\n');
  };

  const getMailtoUrl = (email, job, contactName) => {
    const subject = encodeURIComponent(`Re: ${job.title} Position at ${job.company}`);
    const body = encodeURIComponent(generateEmailBody(job, contactName));
    return `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleCopyScript = async (job) => {
    const script = isEditingScript ? editedScript : (job.phone_script || generateScript(job));
    try {
      await navigator.clipboard.writeText(script);
      setCopiedScript(true);
      setTimeout(() => setCopiedScript(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEditScript = (job) => {
    const currentScript = job.phone_script || generateScript(job);
    setEditedScript(currentScript);
    setEditingScriptJobId(job.id);
    setIsEditingScript(true);
  };

  const handleSaveScript = async (job) => {
    if (!editingScriptJobId || !job) return;
    
    try {
      // Only send the field that changed
      await onUpdate(job.id, { phone_script: editedScript });
      setIsEditingScript(false);
      setEditingScriptJobId(null);
      setEditedScript('');
    } catch (err) {
      console.error('Failed to save phone script:', err);
      setSnackbar({ open: true, message: 'Failed to save phone script. Please try again.', severity: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingScript(false);
    setEditingScriptJobId(null);
    setEditedScript('');
  };

  if (!jobs || jobs.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary">
          No jobs found. Search for jobs or add one manually!
        </Typography>
      </Box>
    );
  }

  const activeJobs = jobs.filter(j => j && !['rejected', 'withdrawn', 'accepted'].includes(j.status));
  const closedJobs = jobs.filter(j => j && ['rejected', 'withdrawn', 'accepted'].includes(j.status));

  const renderJobCard = (job) => {
    if (!job) return null;

    const salary = formatSalary(job.salary_min, job.salary_max, job.salary_offered);
    const isExpanded = selectedJob?.id === job.id;
    const emailPatterns = job.company ? generateEmailPatterns(job.company, job.contact_name) : [];

    return (
      <Card 
        key={job.id}
        variant="light"
        sx={{ 
          mb: 2,
          borderColor: isExpanded ? 'primary.main' : 'grey.200',
          boxShadow: isExpanded ? 2 : 1,
          overflow: 'hidden',
        }}
      >
        {/* Job Summary - Always Visible */}
        <CardContent 
          sx={{ 
            p: 2.5, 
            cursor: 'pointer',
            bgcolor: 'white',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'grey.50' }
          }}
          onClick={() => setSelectedJob(isExpanded ? null : job)}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1, 
                lineHeight: 1.3, 
                pr: 1.5,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'text.primary',
              }}
            >
              {job.title}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(job.id);
              }}
              sx={{ 
                bgcolor: 'error.main',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <DeleteIcon size={16} />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'text.secondary' }}>
            {job.company}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {job.location && (
              <Chip 
                icon={<LocationIcon size={14} />} 
                label={job.location} 
                size="small" 
                variant="outlined"
                sx={{ 
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  bgcolor: 'white',
                  fontSize: '0.75rem',
                }}
              />
            )}
            {salary && (
              <Chip 
                icon={<MoneyIcon size={14} />} 
                label={salary} 
                size="small" 
                variant="outlined"
                sx={{ 
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  bgcolor: 'white',
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Box>
        </CardContent>

        {/* Expanded Detail View */}
        {isExpanded && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200' }}>
            <CardContent sx={{ p: 3, bgcolor: 'grey.50' }}>
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                {job.url && (
                  <Button 
                    variant="contained" 
                    size="large"
                    href={job.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ 
                      flexGrow: 1, 
                      minWidth: '120px',
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 'none' },
                    }}
                  >
                    Apply →
                  </Button>
                )}
                <Button 
                  variant="outlined"
                  color="primary" 
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(job);
                  }}
                  startIcon={<EditIcon size={18} />}
                  sx={{ ...designSystem.button.outlined, minWidth: '100px' }}
                >
                  Edit
                </Button>
                <Button 
                  variant="outlined"
                  color="error" 
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(job.id);
                    setSelectedJob(null);
                  }}
                  startIcon={<DeleteIcon size={18} />}
                  sx={{ ...designSystem.button.outlined, minWidth: '100px' }}
                >
                  Delete
                </Button>
              </Box>

              <Divider sx={{ my: 3, ...designSystem.divider }} />

              {/* Fast Track Contact Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ZapIcon size={20} />
                    <Typography variant="h6" fontWeight="bold">
                      Fast Track Contact
                    </Typography>
                  </Box>
                  {!isPro && (
                    <Chip 
                      label="PRO" 
                      color="secondary" 
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                
                {subscriptionLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : isPro ? (
                  <>
                    {/* Phone / Email Script - Top Priority */}
                    <Box
                      sx={{ 
                        ...designSystem.infoBox.light,
                        mb: 2.5,
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box component="span" sx={{ mt: 0.25, flexShrink: 0, color: 'primary.main' }}>
                        <PhoneIcon size={20} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="600" sx={{ color: 'text.primary' }}>
                            Phone / Email Script
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(!isEditingScript || editingScriptJobId !== job.id) && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditScript(job);
                                  }}
                                  sx={{ 
                                    color: 'text.secondary', 
                                    '&:hover': { bgcolor: 'grey.100', color: 'primary.main' } 
                                  }}
                                >
                                  <EditIcon size={16} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyScript(job);
                                  }}
                                  sx={{ 
                                    color: copiedScript ? 'success.main' : 'text.secondary', 
                                    '&:hover': { bgcolor: 'grey.100', color: 'primary.main' } 
                                  }}
                                >
                                  {copiedScript ? <Check size={16} /> : <CopyIcon size={16} />}
                                </IconButton>
                              </>
                            )}
                            {isEditingScript && editingScriptJobId === job.id && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveScript(job);
                                  }}
                                  sx={{ color: 'success.main', '&:hover': { bgcolor: 'success.50' } }}
                                >
                                  <CheckCircle size={16} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelEdit();
                                  }}
                                  sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}
                                >
                                  <X size={16} />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                        {(!isEditingScript || editingScriptJobId !== job.id) ? (
                          <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary', fontStyle: 'italic' }}>
                            "{job.phone_script || generateScript(job)}"
                          </Typography>
                        ) : (editingScriptJobId === job.id && (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={editedScript}
                            onChange={(e) => setEditedScript(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              mt: 1, 
                              '& .MuiOutlinedInput-root': { 
                                bgcolor: 'grey.50',
                                '&:hover': { bgcolor: 'white' },
                                '&.Mui-focused': { bgcolor: 'white' },
                              } 
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Contact Buttons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<LinkedInIcon size={18} />}
                        href={getLinkedInSearchUrl(job)}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                        fullWidth
                        sx={{ ...designSystem.button.outlined, justifyContent: 'flex-start', px: 2.5 }}
                      >
                        Message LinkedIn Recruiters
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<MapIcon size={18} />}
                        href={getGoogleMapsUrl(job.company, job.location)}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                        fullWidth
                        sx={{ ...designSystem.button.outlined, justifyContent: 'flex-start', px: 2.5 }}
                      >
                        Find Office Location
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<PhoneIcon size={18} />}
                        href={`tel:${job.contact_phone || ''}`}
                        disabled={!job.contact_phone}
                        onClick={(e) => e.stopPropagation()}
                        fullWidth
                        sx={{ ...designSystem.button.outlined, justifyContent: 'flex-start', px: 2.5 }}
                      >
                        {job.contact_phone || 'Add phone number'}
                      </Button>
                    </Box>

                    {/* Email Patterns */}
                    <Box sx={{ ...designSystem.infoBox.light, mb: 2.5 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <EmailIcon size={18} />
                        <Typography variant="subtitle2" fontWeight="600">
                          Try These Emails
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {emailPatterns.slice(0, 4).map((email, i) => (
                          <Button
                            key={i}
                            size="medium"
                            variant="outlined"
                            startIcon={<EmailIcon size={16} />}
                            href={getMailtoUrl(email, job, job.contact_name)}
                            onClick={(e) => e.stopPropagation()}
                            fullWidth
                            sx={{ 
                              justifyContent: 'flex-start', 
                              textTransform: 'none', 
                              fontFamily: 'monospace', 
                              fontSize: '0.8rem',
                              borderColor: 'grey.300',
                              color: 'text.secondary',
                              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                            }}
                          >
                            {email}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </>
                ) : (
                  /* Paywall */
                  <Box sx={{ ...designSystem.infoBox.light, textAlign: 'center', p: 4 }}>
                    <Box display="flex" alignItems="center" justifyContent="center" mb={2.5}>
                      <Box sx={{ 
                        width: 64, 
                        height: 64, 
                        borderRadius: '50%', 
                        bgcolor: 'secondary.light', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}>
                        <LockIcon size={32} strokeWidth={1.5} color="white" />
                      </Box>
                    </Box>
                    <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
                      Unlock Fast Track Contact
                    </Typography>
                    <Typography variant="h5" color="secondary" fontWeight="700" gutterBottom>
                      Just $4/month
                    </Typography>
                    <Box sx={{ mb: 3, textAlign: 'left', display: 'inline-block', mt: 2 }}>
                      {['Message LinkedIn recruiters', 'LinkedIn / email auto-fill messages', 'Phone / email scripts', 'Auto-generated emails', '24/7 AI job monitoring', 'Office locations for walk-ins'].map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Check size={18} color="#1e88e5" />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Button 
                      variant="contained" 
                      color="secondary"
                      size="large"
                      onClick={(e) => {
                        e.stopPropagation();
                        startCheckout();
                      }}
                      disabled={subscriptionLoading}
                      fullWidth
                      sx={{ ...designSystem.button.contained, mt: 2 }}
                    >
                      {subscriptionLoading ? 'Loading...' : 'Go Pro →'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', fontWeight: 500 }}>
                      Cancel anytime. No questions asked.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Notes */}
              {job.notes && (
                <Box sx={{ ...designSystem.infoBox.accent, mt: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                    {job.notes}
                  </Typography>
                </Box>
              )}

              {/* Interview Feedback */}
              {job.interview_feedback && (
                <Box sx={{ 
                  ...designSystem.infoBox.accent, 
                  mt: 2.5,
                  borderColor: 'warning.200',
                  borderLeftColor: 'warning.main',
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Mic size={18} />
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: 'text.primary' }}>
                      Interview Feedback
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                    {job.interview_feedback}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Box>
        )}
      </Card>
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {activeJobs.length > 0 && (
        <Box mb={4}>
          <Typography 
            variant="h6" 
            fontWeight="600" 
            gutterBottom 
            sx={{ 
              mb: 2.5,
              color: 'text.primary',
              fontSize: '1.1rem',
            }}
          >
            Active ({activeJobs.length})
          </Typography>
          {activeJobs.map(renderJobCard)}
        </Box>
      )}
      
      {closedJobs.length > 0 && (
        <Box>
          <Typography 
            variant="h6" 
            fontWeight="600" 
            color="text.secondary" 
            gutterBottom
            sx={{ 
              mb: 2.5,
              fontSize: '1.1rem',
            }}
          >
            Closed ({closedJobs.length})
          </Typography>
          {closedJobs.map(renderJobCard)}
        </Box>
      )}
    </Box>
  );
}

export default JobList;
